import React, {useState, useContext, useEffect} from 'react';
import classnames from 'classnames';
import {TracksContext} from 'context/TracksContext';
import {BaseDialog} from "components/common/Dialogs";
import {TextField} from "../../common/Form";
import styles from '../../common/Form/TextField.module.scss';
import {BounceLoader} from "react-spinners";

function getInitialVersionType(editedElem) {
    let versionType = editedElem.versionType || '';
    const subversionType = editedElem.subversionType || '';

    if (subversionType) {
        const suffix = `(${subversionType})`;
        if (versionType.includes(suffix)) {
            versionType = versionType.replace(new RegExp(`\\s*\\(${subversionType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)\\s*$`), '').trim();
        }
    }

    return versionType;
}

function getDisplayArtist(elem) {
    return elem.artiste || elem.artist || '';
}

export default function UpdateTrackDialog({onClose, onSave, editedElem}) {
    if (editedElem && editedElem.status && editedElem.status.error) {
        onClose();
    }
    const {
        genresResult: {genres, versions},
        allPresets,
        fetchPresets,
        getGenresAndCategories,
        updateTracksNameGenre,
        updateTrackVersionMeta,
        isUpdatingTrack
    } = useContext(TracksContext);
    const displayArtist = getDisplayArtist(editedElem);
    const initFields = editedElem.isVersion
        ? {...editedElem, versionType: getInitialVersionType(editedElem), artist: getDisplayArtist(editedElem), artiste: displayArtist}
        : {...editedElem, artist: displayArtist, artiste: displayArtist};
    const [fields, setFields] = useState(initFields);
    const [presets, setPresets] = useState([]);

    useEffect(() => {
        if (!versions || versions.length === 0) {
            getGenresAndCategories();
        }
    }, [versions, getGenresAndCategories]);

    useEffect(() => {
        if (typeof allPresets === 'undefined') {
            fetchPresets();
        }
    }, [allPresets, fetchPresets]);

    useEffect(() => {
        if (typeof allPresets !== 'undefined' && presets.length === 0) {
            setPresets(allPresets);
        }
    }, [allPresets, presets.length]);

    const versionOptions = (() => {
        const options = [...(versions || [])];
        if (fields.versionType && !options.includes(fields.versionType)) {
            options.unshift(fields.versionType);
        }
        return options;
    })();

    return (
        <BaseDialog
            id="addNewVideoDialog"
            label={editedElem.isVersion ? "Edit Version" : "Edit Track"}
            onClose={()=>{onClose()}}>
            <div style={{ position: 'relative' }}>
                {!editedElem.isVersion && (
                    <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666', lineHeight: 1.4 }}>
                        Edit the parent track (song name, artist, genre, preset).
                        For Instrumental, Acapella, Radio Edit, etc., open AOIR (A/O/I/R) and use <strong>Edit Version</strong>.
                        Do not put version tags in the song name.
                    </p>
                )}
                {editedElem.isVersion && (
                    <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666', lineHeight: 1.4 }}>
                        Edit this AOIR version only (version type, BPM, etc.).
                        Keep the song name as the base title without version brackets.
                    </p>
                )}
                {isUpdatingTrack && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1000,
                        background: 'rgba(255, 255, 255, 0.67)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            width: '158px',
                            display: 'block',
                            margin: 'auto',
                            textAlign: 'center'
                        }}>
                            <BounceLoader loading={true} size={60} color="#34bfa3" />
                            <span className="loading-text" style={{ display: 'block', marginTop: '10px' }}>Updating track...</span>
                        </div>
                    </div>
                )}
                <form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        if (editedElem && editedElem.isVersion) {
                            await updateTrackVersionMeta(fields);
                        } else {
                            await updateTracksNameGenre(fields);
                        }
                        onSave && await onSave();
                        onClose();
                    }}
                    style={{ margin: 0 }}
                >
                <div className="form-group" style={{ marginBottom: '20px' }}>
                    <TextField
                        key="songName"
                        label="Song Name"
                        required
                        placeholder="Song Name"
                        name="songName"
                        defaultValue={fields.songName}
                        onChange={event => setFields({...fields, songName: event.target.value})}/>
                    <TextField
                        key="Artist"
                        label="Artist"
                        required
                        placeholder="Artist Name"
                        name="artist"
                        defaultValue={fields.artiste || fields.artist}
                        onChange={event => setFields({...fields, artist: event.target.value, artiste: event.target.value})}/>

                    {editedElem.isVersion && (
                        <div
                            className={classnames(styles['form-text-field'], 'form-group m-form__group row ')}
                            style={{ marginBottom: '20px' }}
                        >
                            <label
                                htmlFor="versionType"
                                className='col-12 mb-3'
                                style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}
                            >
                                Version:
                            </label>
                            <select
                                required
                                value={fields.versionType || ""}
                                onChange={event => setFields({...fields, versionType: event.target.value})}
                                className="form-control"
                                id="versionType"
                                style={{
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    padding: '10px 15px',
                                    fontSize: '14px',
                                    transition: 'border-color 0.3s'
                                }}
                            >
                                <option disabled value=""> -- select an option --</option>
                                {versionOptions.map((version, key) => (
                                    <option key={key} value={version}>{version}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Only show Genre & Preset for main track edits, not for child/version edits */}
                    {(!editedElem.isVersion) && (
                        <>
                            <div
                                className={classnames(styles['form-text-field'], 'form-group m-form__group row ')}
                                style={{ marginBottom: '20px' }}
                            >
                                <label
                                    htmlFor="genre"
                                    className='col-12 mb-3'
                                    style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}
                                >
                                    Genre:
                                </label>
                                <select
                                    required
                                    value={fields.genre || ""}
                                    onChange={event => setFields({...fields, genre: event.target.value})}
                                    className="form-control"
                                    id="genre"
                                    style={{
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        padding: '10px 15px',
                                        fontSize: '14px',
                                        transition: 'border-color 0.3s'
                                    }}
                                >
                                    <option disabled value=""> -- select an option --</option>
                                    {genres.map((genre, key) => (
                                        <option key={key} value={genre}>{genre}</option>
                                    ))}
                                </select>
                            </div>

                            <div
                                className={classnames(styles['form-text-field'], 'form-group m-form__group row ')}
                                style={{ marginBottom: '20px' }}
                            >
                                <label
                                    htmlFor="preset"
                                    className='col-12 mb-3'
                                    style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}
                                >
                                    Preset:
                                </label>
                                <select
                                    required
                                    value={fields.preset || fields.presetId || ""}
                                    onChange={event => setFields({...fields, preset: event.target.value})}
                                    className="form-control"
                                    id="preset"
                                    style={{
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        padding: '10px 15px',
                                        fontSize: '14px',
                                        transition: 'border-color 0.3s'
                                    }}
                                >
                                    <option disabled value=""> -- select an option --</option>
                                    {(presets.length !== 0) && presets.map((preset, key) => (
                                        <option key={key} value={preset.id}>{preset.name}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    <TextField
                        required
                        key="BPM"
                        label="BPM"
                        placeholder="100"
                        type="number"
                        name="bpm"
                        defaultValue={fields.bpm}
                        onChange={event => setFields({...fields, bpm: event.target.value})}/>
                </div>
                <div className="d-flex flex-row justify-content-between row" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => onClose()}
                        data-dismiss="modal"
                        style={{
                            minWidth: '120px',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            transition: 'all 0.3s',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                        }}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        data-dismiss="modal"
                        style={{
                            minWidth: '120px',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            transition: 'all 0.3s',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                            background: 'linear-gradient(135deg, #34bfa3 0%, #259d85 100%)',
                            border: 'none'
                        }}>
                        Save Changes
                    </button>
                </div>
            </form>
            </div>
        </BaseDialog>)
}
